import React, { useState, useEffect } from 'react';
import studentLessonService from '../../services/student/studentLessonService';

export default function StudentLessonsPage() {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, past, future
  const [expandedLessons, setExpandedLessons] = useState(new Set());
  const [totals, setTotals] = useState({
    totalAmount: 0,
    lessonCount: 0
  });

  useEffect(() => {
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    try {
      setLoading(true);
      const response = await studentLessonService.getMyLessons();
      const lessonsData = response.lessons || [];
      
      // Toplam hesapla ve her derse ekle
      let total = 0;
      lessonsData.forEach(lesson => {
        const dersUcret = parseFloat(lesson.ders_ucret || 0);
        const sirketPayi = parseFloat(lesson.sirket_payi || 0);
        const danisman1 = parseFloat(lesson.danisman1 || 0);
        const danisman2 = parseFloat(lesson.danisman2 || 0);
        const danisman3 = parseFloat(lesson.danisman3 || 0);
        
        const lessonTotal = dersUcret + sirketPayi + danisman1 + danisman2 + danisman3;
        lesson.totalAmount = lessonTotal; // Her derse toplam ekle
        total += lessonTotal;
      });
      
      setTotals({
        totalAmount: total,
        lessonCount: lessonsData.length
      });
      
      setLessons(lessonsData);
    } catch (error) {
      console.error('Dersler yüklenirken hata:', error);
      alert('Dersler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Dersleri filtrele
  const getFilteredLessons = () => {
    const now = new Date();
    
    return lessons.filter(lesson => {
      const lessonDate = new Date(lesson.baslangic_tarihi);
      
      if (filter === 'past') {
        return lessonDate < now;
      } else if (filter === 'future') {
        return lessonDate >= now;
      }
      return true; // all
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return date.toLocaleString('tr-TR', options);
  };

  const formatDuration = (duration) => {
    return `${duration} dakika`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(amount || 0));
  };

  const toggleExpand = (lessonId) => {
    const newExpanded = new Set(expandedLessons);
    if (newExpanded.has(lessonId)) {
      newExpanded.delete(lessonId);
    } else {
      newExpanded.add(lessonId);
    }
    setExpandedLessons(newExpanded);
  };

  const getLessonStatus = (dateString) => {
    const lessonDate = new Date(dateString);
    const now = new Date();
    
    if (lessonDate < now) {
      return { text: 'Tamamlandı', color: 'text-gray-500 bg-gray-100' };
    } else {
      return { text: 'Yaklaşan', color: 'text-green-600 bg-green-100' };
    }
  };

  const filteredLessons = getFilteredLessons();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Dersler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Derslerim</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'all' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Tümü ({lessons.length})
          </button>
          <button
            onClick={() => setFilter('future')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'future' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Yaklaşan
          </button>
          <button
            onClick={() => setFilter('past')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'past' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Geçmiş
          </button>
        </div>
      </div>

      {filteredLessons.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 text-lg">
            {filter === 'all' 
              ? 'Henüz ders kaydınız bulunmamaktadır.'
              : filter === 'future'
              ? 'Yaklaşan ders bulunmamaktadır.'
              : 'Geçmiş ders bulunmamaktadır.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredLessons.map((lesson) => {
            const status = getLessonStatus(lesson.baslangic_tarihi);
            const isExpanded = expandedLessons.has(lesson.id);
            
            return (
              <div key={lesson.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold">
                          {lesson.ders_adi || 'Ders'}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${status.color}`}>
                          {status.text}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div>
                          <p className="text-sm text-gray-600">Öğretmen</p>
                          <p className="font-medium">{lesson.ogretmen_adi || '-'}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-600">Tarih ve Saat</p>
                          <p className="font-medium">{formatDate(lesson.baslangic_tarihi)}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-600">Süre</p>
                          <p className="font-medium">{formatDuration(lesson.ders_sure)}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-600">Ders Konusu</p>
                          <p className="font-medium">{lesson.ders_konusu || '-'}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-600">Resim</p>
                          <p className="font-medium">{lesson.dosya ? 'Var' : 'Yok'}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-600">Toplam Ücret</p>
                          <p className="font-bold text-lg text-red-600">
                            {formatCurrency(
                              parseFloat(lesson.ders_ucret || 0) +
                              parseFloat(lesson.sirket_payi || 0) +
                              parseFloat(lesson.danisman1 || 0) +
                              parseFloat(lesson.danisman2 || 0) +
                              parseFloat(lesson.danisman3 || 0)
                            )}
                          </p>
                          <button
                            onClick={() => toggleExpand(lesson.id)}
                            className="text-xs text-blue-600 hover:text-blue-800 mt-1"
                          >
                            Ücret detayı için tıklayın
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Genişletilmiş Detay Bölümü - Öğrenci için sadeleştirilmiş */}
                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50 p-6">
                    <h4 className="text-lg font-semibold mb-4 text-gray-800">📊 Ders Bilgileri</h4>
                    
                    <div className="bg-white rounded-lg p-4">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2">
                          <span className="text-gray-600">Ders Ücreti:</span>
                          <span className="font-bold text-lg text-red-600">
                            {formatCurrency(
                              parseFloat(lesson.ders_ucret || 0) +
                              parseFloat(lesson.sirket_payi || 0) +
                              parseFloat(lesson.danisman1 || 0) +
                              parseFloat(lesson.danisman2 || 0) +
                              parseFloat(lesson.danisman3 || 0)
                            )}
                          </span>
                        </div>
                        
                        <div className="flex justify-between items-center py-2">
                          <span className="text-gray-600">Ders Süresi:</span>
                          <span className="font-medium">{lesson.ders_sure} dakika</span>
                        </div>
                        
                        {lesson.ders_konusu && (
                          <div className="flex justify-between items-center py-2">
                            <span className="text-gray-600">Konu:</span>
                            <span className="font-medium">{lesson.ders_konusu}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-start">
                          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="text-sm text-blue-800">
                            <strong>Bilgi:</strong> Bu ders için toplam ücretinizdir.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}